import React, { useCallback, useId } from 'react';
import { useIntl } from 'react-intl';
import type { MediaWidgetOptions } from 'fym-shared/src/smartlinks/mediaWidget';
import { TypedMemo } from '../../../../../common/types';
import { SettingsSwitchRow } from '../../../../../music24/promote/composer/settings/SettingsSwitchRow';
import { mediaBlockMessages } from '../mediaBlockMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Media block editor; each part uses its own slice of it */
import styles from './MediaWidgetEditionView.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MediaPlaybackOptionsProps {
  options: MediaWidgetOptions;
  setOption: <K extends keyof MediaWidgetOptions>(key: K, value: MediaWidgetOptions[K]) => void;
}

/** The Playback options of a video (design a2): muted autoplay, loop and the controls. */
const MediaPlaybackOptionsComponent: React.FC<MediaPlaybackOptionsProps> = ({ options, setOption }) => {
  const intl = useIntl();
  const id = useId();
  const setAutoplay = useCallback((value: boolean) => setOption('autoplay', value), [setOption]);
  const setLoop = useCallback((value: boolean) => setOption('loop', value), [setOption]);
  const setControls = useCallback((value: boolean) => setOption('controls', value), [setOption]);

  return (
    <section className={styles.section} data-testid="media-playback">
      <h6 className={styles.sectionTitle}>{intl.formatMessage(mediaBlockMessages.playback)}</h6>
      <SettingsSwitchRow
        id={`${id}-autoplay`}
        title={intl.formatMessage(mediaBlockMessages.autoplay)}
        note={intl.formatMessage(mediaBlockMessages.autoplayNote)}
        checked={options.autoplay}
        onChange={setAutoplay}
      />
      <SettingsSwitchRow
        id={`${id}-loop`}
        title={intl.formatMessage(mediaBlockMessages.loop)}
        checked={options.loop}
        onChange={setLoop}
      />
      <SettingsSwitchRow
        id={`${id}-controls`}
        title={intl.formatMessage(mediaBlockMessages.controls)}
        note={intl.formatMessage(mediaBlockMessages.controlsNote)}
        checked={options.controls}
        onChange={setControls}
      />
    </section>
  );
};

export const MediaPlaybackOptions = TypedMemo(MediaPlaybackOptionsComponent);
